import { of, EMPTY, fromEvent, merge } from 'rxjs';
import { switchMap, catchError, tap, defaultIfEmpty } from 'rxjs/operators';
import { fetchJson, postJson, deleteEntity } from './http.js';
import { renderTemplate, replaceViewContainer, showError } from './ui.js';

/**
 * Renders a list of topics into the given <ul> element. Clears the list
 * first, then appends one <li> per topic with a name span and a delete button.
 */
function renderTopics(listEl, topics) {
    listEl.innerHTML = '';
    for (let topic of topics) {
        let li = document.createElement('li');
        li.innerHTML = `<span class="topic-name">${topic.name}</span><a class="cards-for-topic-btn" href="#cards?topic_id=${topic.id}">Karten</a><button class="delete-topic-btn" data-id="${topic.id}">Löschen</button>`;
        listEl.appendChild(li);
    }
}

/**
 * Creates and returns the topics view Observable. Fetches all topics from
 * GET /api/topics on mount, renders them, and wires up the add form
 * (button click and Enter key) and per-row delete buttons (event delegation).
 */
export function createTopicsView() {
    return of(undefined).pipe(
        switchMap(() => {
            let el = renderTemplate('topics-view');
            replaceViewContainer(el);

            let nameInput = el.querySelector('.topic-name-input');
            let addBtn = el.querySelector('.add-topic-btn');
            let statusEl = el.querySelector('.status');
            let listEl = el.querySelector('.topics-list');

            function fetchAndRender() {
                return fetchJson('/api/topics').pipe(
                    tap((topics) => renderTopics(listEl, topics)),
                    catchError((err) => {
                        showError(err);
                        return EMPTY;
                    })
                );
            }

            let load$ = fetchAndRender();

            function submitAdd() {
                let name = nameInput.value.trim();
                if (!name) {
                    statusEl.textContent = 'Bitte einen Namen eingeben.';
                    return EMPTY;
                }
                statusEl.textContent = 'Wird gespeichert…';
                return postJson('/api/topics', { name }).pipe(
                    switchMap(() => {
                        nameInput.value = '';
                        statusEl.textContent = 'Thema hinzugefügt.';
                        return fetchAndRender();
                    }),
                    catchError((err) => {
                        showError(err);
                        return EMPTY;
                    })
                );
            }

            let add$ = fromEvent(addBtn, 'click').pipe(
                switchMap(() => submitAdd())
            );

            let enter$ = fromEvent(nameInput, 'keydown').pipe(
                switchMap((e) => (e.key === 'Enter' ? submitAdd() : EMPTY))
            );

            let delete$ = fromEvent(listEl, 'click').pipe(
                switchMap((e) => {
                    let btn = e.target.closest('.delete-topic-btn');
                    if (!btn) {
                        return EMPTY;
                    }
                    if (!window.confirm('Thema wirklich löschen?')) {
                        return EMPTY;
                    }
                    let id = btn.dataset.id;
                    return deleteEntity(`/api/topics/${id}`).pipe(
                        defaultIfEmpty(null),
                        switchMap(() => fetchAndRender()),
                        catchError((err) => {
                            showError(err);
                            return EMPTY;
                        })
                    );
                })
            );

            return merge(load$, add$, enter$, delete$);
        })
    );
}
